import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import LeadCaptureBox from "@/components/LeadCaptureBox";
import EmailGateModal from "@/components/EmailGateModal";
import { SITE_URL } from "@/lib/siteConfig";
import {
  ArrowRight, FileText, Check, AlertTriangle, X,
  Type, Eye, Smartphone, Target, MessageSquare,
  BarChart3, Zap, ChevronDown, ChevronUp, Sparkles,
  Users, Clock,
} from "lucide-react";

// ── Analysis logic ──

interface AnalysisResult {
  overallScore: number;
  grade: string;
  gradeColor: string;
  categories: AnalysisCategory[];
  suggestions: string[];
  stats: { words: number; sentences: number; paragraphs: number; readingTime: string; charCount: number };
}

interface AnalysisCategory {
  name: string;
  score: number;
  status: "good" | "warning" | "critical";
  icon: any;
  findings: string[];
}

const SPAM_WORDS = [
  "free", "winner", "congratulations", "act now", "limited time", "urgent",
  "click here", "buy now", "order now", "special promotion", "no obligation",
  "risk free", "guarantee", "no cost", "cash", "discount", "lowest price",
  "100%", "amazing", "incredible offer", "double your", "earn money",
  "extra income", "million", "billion", "!!!",  "$$$", "all caps",
];

const POWER_WORDS = [
  "you", "your", "because", "new", "now", "how", "proven", "easy",
  "discover", "imagine", "results", "secret", "exclusive", "introducing",
  "save", "love", "breakthrough", "instantly",
];

const PERSONALIZATION_TOKENS = [
  "{{first_name}}", "{{full_name}}", "{{name}}", "{{company}}",
  "{first_name}", "{name}", "{company}",
];

function analyzeEmail(subject: string, body: string): AnalysisResult {
  const subjectLower = subject.toLowerCase();
  const bodyLower = body.toLowerCase();
  const fullText = `${subject} ${body}`;
  const fullLower = fullText.toLowerCase();

  // Basic stats
  const words = body.split(/\s+/).filter(w => w.length > 0);
  const wordCount = words.length;
  const sentences = body.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const sentenceCount = Math.max(sentences.length, 1);
  const paragraphs = body.split(/\n\s*\n/).filter(p => p.trim().length > 0);
  const avgWordsPerSentence = wordCount / sentenceCount;
  const readingTimeMin = Math.max(1, Math.ceil(wordCount / 200));

  const categories: AnalysisCategory[] = [];
  const suggestions: string[] = [];

  // ── 1. Subject Line ──
  const subjectFindings: string[] = [];
  let subjectScore = 70;

  if (subject.length === 0) {
    subjectScore = 0;
    subjectFindings.push("No subject line provided.");
  } else {
    if (subject.length <= 60) {
      subjectScore += 10;
      subjectFindings.push(`Length is ${subject.length} characters — fits most inboxes.`);
    } else {
      subjectScore -= 15;
      subjectFindings.push(`Subject is ${subject.length} characters — may get truncated. Aim for under 60.`);
      suggestions.push("Shorten your subject line to under 60 characters.");
    }

    if (subject === subject.toUpperCase() && subject.length > 3) {
      subjectScore -= 20;
      subjectFindings.push("Subject is ALL CAPS — this triggers spam filters and feels aggressive.");
      suggestions.push("Avoid writing your subject line in all caps.");
    }

    const subjectEmojis = (subject.match(/[\u{1F600}-\u{1F9FF}]/gu) || []).length;
    if (subjectEmojis === 1) {
      subjectScore += 5;
      subjectFindings.push("One emoji detected — can boost open rates when used well.");
    } else if (subjectEmojis > 2) {
      subjectScore -= 10;
      subjectFindings.push(`${subjectEmojis} emojis detected — too many can look spammy.`);
    }

    const hasPersonalization = PERSONALIZATION_TOKENS.some(t => subject.includes(t));
    if (hasPersonalization) {
      subjectScore += 10;
      subjectFindings.push("Personalization token found — great for open rates.");
    }

    if (/\?$/.test(subject.trim())) {
      subjectScore += 5;
      subjectFindings.push("Question format — drives curiosity and opens.");
    }

    if (/^\d/.test(subject.trim())) {
      subjectScore += 5;
      subjectFindings.push("Starts with a number — proven to attract attention.");
    }
  }

  categories.push({
    name: "Subject Line",
    score: Math.max(0, Math.min(100, subjectScore)),
    status: subjectScore >= 70 ? "good" : subjectScore >= 45 ? "warning" : "critical",
    icon: Type,
    findings: subjectFindings,
  });

  // ── 2. Readability ──
  const readFindings: string[] = [];
  let readScore = 70;

  if (wordCount < 20) {
    readScore -= 15;
    readFindings.push("Very short email. Consider adding more context or value.");
    suggestions.push("Your email is very short. Add more substance to deliver value.");
  } else if (wordCount <= 200) {
    readScore += 15;
    readFindings.push(`${wordCount} words — ideal length for marketing emails.`);
  } else if (wordCount <= 400) {
    readScore += 5;
    readFindings.push(`${wordCount} words — acceptable but could be tighter.`);
  } else {
    readScore -= 10;
    readFindings.push(`${wordCount} words — quite long. Consider cutting to under 200 for higher engagement.`);
    suggestions.push("Your email is over 400 words. Shorter emails tend to get better engagement.");
  }

  if (avgWordsPerSentence <= 15) {
    readScore += 10;
    readFindings.push("Short sentences — easy to scan on mobile.");
  } else if (avgWordsPerSentence <= 22) {
    readFindings.push("Moderate sentence length. Some sentences could be split for readability.");
  } else {
    readScore -= 10;
    readFindings.push("Long sentences detected. Break them up for easier reading.");
    suggestions.push("Break up long sentences. Aim for 15 words or fewer per sentence.");
  }

  if (paragraphs.length >= 3) {
    readScore += 5;
    readFindings.push(`${paragraphs.length} paragraphs — good visual breaks.`);
  } else if (wordCount > 50) {
    readScore -= 5;
    readFindings.push("Few paragraph breaks. Add white space for scannability.");
    suggestions.push("Add paragraph breaks between ideas. Dense blocks of text get skipped.");
  }

  categories.push({
    name: "Readability",
    score: Math.max(0, Math.min(100, readScore)),
    status: readScore >= 70 ? "good" : readScore >= 45 ? "warning" : "critical",
    icon: Eye,
    findings: readFindings,
  });

  // ── 3. Spam Risk ──
  const spamFindings: string[] = [];
  let spamScore = 90;

  const foundSpamWords: string[] = [];
  for (const word of SPAM_WORDS) {
    if (fullLower.includes(word)) foundSpamWords.push(word);
  }

  if (foundSpamWords.length === 0) {
    spamFindings.push("No common spam trigger words detected.");
  } else if (foundSpamWords.length <= 2) {
    spamScore -= 10;
    spamFindings.push(`Minor spam triggers found: ${foundSpamWords.join(", ")}. Use sparingly.`);
  } else {
    spamScore -= foundSpamWords.length * 8;
    spamFindings.push(`${foundSpamWords.length} spam triggers found: ${foundSpamWords.slice(0, 5).join(", ")}${foundSpamWords.length > 5 ? "..." : ""}. High risk of spam filtering.`);
    suggestions.push("Reduce spam trigger words. You have " + foundSpamWords.length + " that could flag filters.");
  }

  const exclamationCount = (fullText.match(/!/g) || []).length;
  if (exclamationCount > 3) {
    spamScore -= 10;
    spamFindings.push(`${exclamationCount} exclamation marks — excessive punctuation triggers filters.`);
    suggestions.push("Reduce exclamation marks. More than 2-3 can trigger spam filters.");
  }

  const capsWords = words.filter(w => w.length > 3 && w === w.toUpperCase());
  const capsPct = wordCount > 0 ? (capsWords.length / wordCount) * 100 : 0;
  if (capsPct > 10) {
    spamScore -= 15;
    spamFindings.push("Excessive ALL CAPS words detected. This is a strong spam signal.");
  }

  categories.push({
    name: "Spam Risk",
    score: Math.max(0, Math.min(100, spamScore)),
    status: spamScore >= 70 ? "good" : spamScore >= 45 ? "warning" : "critical",
    icon: AlertTriangle,
    findings: spamFindings,
  });

  // ── 4. Personalization ──
  const persFindings: string[] = [];
  let persScore = 50;

  const foundTokens = PERSONALIZATION_TOKENS.filter(t => fullText.includes(t));
  if (foundTokens.length >= 2) {
    persScore = 95;
    persFindings.push(`${foundTokens.length} personalization tokens found: ${foundTokens.join(", ")}. Excellent!`);
  } else if (foundTokens.length === 1) {
    persScore = 75;
    persFindings.push(`1 personalization token found: ${foundTokens[0]}. Consider adding more.`);
  } else {
    persScore = 30;
    persFindings.push("No personalization tokens detected. Adding {{first_name}} can boost engagement by 10-20%.");
    suggestions.push("Add personalization tokens like {{first_name}} to increase engagement.");
  }

  // Check for "you/your" language (reader-focused)
  const youCount = (bodyLower.match(/\byou\b|\byour\b/g) || []).length;
  const weCount = (bodyLower.match(/\bwe\b|\bour\b|\bi\b/g) || []).length;
  if (youCount > weCount && youCount >= 2) {
    persScore += 5;
    persFindings.push("Good reader-focused language — more 'you/your' than 'we/our'.");
  } else if (weCount > youCount * 2) {
    persScore -= 5;
    persFindings.push("Too self-focused. Use more 'you/your' language and less 'we/our'.");
    suggestions.push("Shift from 'we/our' to 'you/your' language. Readers care about their benefits.");
  }

  categories.push({
    name: "Personalization",
    score: Math.max(0, Math.min(100, persScore)),
    status: persScore >= 65 ? "good" : persScore >= 40 ? "warning" : "critical",
    icon: Users,
    findings: persFindings,
  });

  // ── 5. CTA Effectiveness ──
  const ctaFindings: string[] = [];
  let ctaScore = 50;

  const ctaPatterns = /\b(click|sign up|buy|shop|get|start|try|learn more|download|register|subscribe|join|book|grab|claim|enroll|order|reserve)\b/gi;
  const ctaMatches = fullLower.match(ctaPatterns) || [];
  const hasCtaButton = /\[.*?\]|\bcta\b|button/i.test(fullText);

  if (ctaMatches.length >= 1) {
    ctaScore += 20;
    ctaFindings.push(`CTA language detected: "${ctaMatches[0]}". Good — clear direction for the reader.`);
  }
  if (ctaMatches.length >= 3) {
    ctaScore += 10;
    ctaFindings.push("Multiple CTAs found. Make sure one is the primary action.");
  }
  if (ctaMatches.length === 0) {
    ctaScore = 20;
    ctaFindings.push("No clear call-to-action detected. Every email should tell the reader what to do next.");
    suggestions.push("Add a clear call-to-action. Tell readers exactly what to do next.");
  }

  // Check for urgency/scarcity language
  const urgencyWords = ["today", "now", "limited", "expires", "deadline", "last chance", "hurry", "don't miss"];
  const hasUrgency = urgencyWords.some(w => fullLower.includes(w));
  if (hasUrgency) {
    ctaScore += 10;
    ctaFindings.push("Urgency language detected — creates motivation to act now.");
  }

  // Power words
  const foundPowerWords = POWER_WORDS.filter(w => fullLower.includes(w));
  if (foundPowerWords.length >= 3) {
    ctaScore += 5;
    ctaFindings.push(`${foundPowerWords.length} power words used — strong persuasive copy.`);
  }

  categories.push({
    name: "CTA & Persuasion",
    score: Math.max(0, Math.min(100, ctaScore)),
    status: ctaScore >= 65 ? "good" : ctaScore >= 40 ? "warning" : "critical",
    icon: Target,
    findings: ctaFindings,
  });

  // ── Overall score ──
  const weights = [20, 20, 25, 15, 20]; // subject, readability, spam, personalization, CTA
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  const weightedSum = categories.reduce((sum, cat, i) => sum + cat.score * weights[i], 0);
  const overall = Math.round(weightedSum / totalWeight);

  const grade = overall >= 90 ? "A" : overall >= 75 ? "B" : overall >= 60 ? "C" : overall >= 40 ? "D" : "F";
  const gradeColor = overall >= 75 ? "text-emerald-600" : overall >= 50 ? "text-amber-500" : "text-red-500";

  return {
    overallScore: overall,
    grade,
    gradeColor,
    categories,
    suggestions: suggestions.slice(0, 5),
    stats: {
      words: wordCount,
      sentences: sentenceCount,
      paragraphs: paragraphs.length,
      readingTime: `${readingTimeMin} min`,
      charCount: body.length,
    },
  };
}

const STATUS_STYLE = {
  good: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: Check },
  warning: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: AlertTriangle },
  critical: { bg: "bg-red-50", border: "border-red-200", text: "text-red-600", icon: X },
};

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Email Copy Analyzer",
  applicationCategory: "BusinessApplication",
  description: "Free email copy analyzer. Paste your marketing email and get instant scoring on readability, spam risk, personalization, CTA effectiveness, and subject line quality.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  operatingSystem: "Web",
};

export default function EmailCopyAnalyzerPage() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [expandedCat, setExpandedCat] = useState<number | null>(null);
  const [showGate, setShowGate] = useState(false);

  const result = useMemo(() => {
    if (!showResults || (!subject.trim() && !body.trim())) return null;
    return analyzeEmail(subject, body);
  }, [subject, body, showResults]);

  function handleAnalyze() {
    if (!body.trim() && !subject.trim()) return;
    setShowGate(true);
  }

  function handleGateComplete() {
    setShowGate(false);
    setShowResults(true);
    setExpandedCat(null);
  }

  function handleReset() {
    setShowResults(false);
    setSubject("");
    setBody("");
    setExpandedCat(null);
  }

  return (
    <>
      {showGate && (
        <EmailGateModal
          toolName="email copy analysis"
          source="/email-copy-analyzer"
          onComplete={handleGateComplete}
          onClose={() => setShowGate(false)}
        />
      )}
      <SeoHead
        title="Free Email Copy Analyzer — Score Your Marketing Emails Instantly | MoreMail"
        description="Paste your email copy and get an instant score on readability, spam risk, personalization, CTA effectiveness, and subject line quality. Free tool — no signup required."
        canonical={`${SITE_URL}/email-copy-analyzer`}
        keywords="email copy analyzer, email content checker, email readability score, email spam checker, email CTA analyzer, marketing email scorer, email quality checker, email writing tool"
        ogTitle="Email Copy Analyzer — Instant Score for Your Marketing Emails | MoreMail"
        ogDescription="Paste your email. Get scored on readability, spam risk, personalization, and CTA strength. Free and instant."
        jsonLd={JSON_LD}
      />

      {/* Hero */}
      <section className="pt-12 pb-6 bg-gradient-to-b from-ink to-[#162029] text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-semibold mb-5 backdrop-blur-sm">
            <FileText size={13} className="text-coral" /> Free Tool
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight mb-3">
            Email Copy <span className="text-coral">Analyzer</span>
          </h1>
          <p className="text-white/50 text-base max-w-lg mx-auto">
            Paste your email copy below. Get an instant score on readability, spam risk, personalization, CTA strength, and subject line quality.
          </p>
        </div>
      </section>

      {/* Analyzer */}
      <section className="py-10 bg-paper">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-6">

            {/* Input */}
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-sand p-5">
                <h2 className="font-display font-bold text-ink text-sm mb-4 flex items-center gap-2">
                  <Type size={15} className="text-coral" /> Paste Your Email
                </h2>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-ink mb-1">Subject Line</label>
                    <input
                      type="text" value={subject} onChange={(e) => { setSubject(e.target.value); setShowResults(false); }}
                      placeholder="Your email subject line..."
                      className="w-full px-3 py-2 rounded-lg border border-sand text-sm font-body bg-white focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-ink mb-1">Email Body</label>
                    <textarea
                      value={body} onChange={(e) => { setBody(e.target.value); setShowResults(false); }}
                      placeholder="Paste your email body copy here..."
                      rows={12}
                      className="w-full px-3 py-2 rounded-lg border border-sand text-sm font-body bg-white focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral transition-all resize-y"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button onClick={handleAnalyze} disabled={!body.trim() && !subject.trim()}
                      className="flex-1 flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white px-5 py-2.5 rounded-lg text-sm font-bold transition-colors disabled:opacity-50">
                      <Sparkles size={15} /> Analyze My Email
                    </button>
                    {showResults && (
                      <button onClick={handleReset}
                        className="px-4 py-2.5 rounded-lg border border-sand text-sm font-medium text-slate hover:bg-paper transition-colors">
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-4">
              {!result ? (
                <div className="bg-white rounded-xl border border-sand p-8 text-center">
                  <FileText size={40} className="mx-auto text-slate/20 mb-3" />
                  <p className="text-sm text-slate">Paste your email and click "Analyze" to see your score.</p>
                  <p className="text-xs text-slate/50 mt-2">We check readability, spam risk, personalization, CTA strength, and subject line quality.</p>
                </div>
              ) : (
                <>
                  {/* Overall score */}
                  <div className="bg-white rounded-xl border border-sand p-5 flex items-center gap-5">
                    <div className="relative w-20 h-20 flex-shrink-0">
                      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                        <circle cx="50" cy="50" r="42" fill="none" stroke="#E5E7EB" strokeWidth="8" />
                        <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8"
                          className={result.gradeColor.replace("text-", "stroke-")}
                          strokeDasharray={`${result.overallScore * 2.64} 264`}
                          strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className={`font-display font-bold text-2xl ${result.gradeColor}`}>{result.overallScore}</span>
                        <span className="text-[9px] text-slate">/ 100</span>
                      </div>
                    </div>
                    <div>
                      <p className={`font-display font-bold text-lg ${result.gradeColor}`}>Grade: {result.grade}</p>
                      <div className="flex gap-4 mt-1 text-[11px] text-slate">
                        <span>{result.stats.words} words</span>
                        <span>{result.stats.sentences} sentences</span>
                        <span>{result.stats.readingTime} read</span>
                      </div>
                    </div>
                  </div>

                  {/* Category cards */}
                  {result.categories.map((cat, i) => {
                    const style = STATUS_STYLE[cat.status];
                    const StatusIcon = style.icon;
                    const CatIcon = cat.icon;
                    const isExpanded = expandedCat === i;
                    return (
                      <div key={cat.name} className={`rounded-xl border overflow-hidden ${style.bg} ${style.border}`}>
                        <button onClick={() => setExpandedCat(isExpanded ? null : i)}
                          className="w-full flex items-center gap-3 p-4 text-left">
                          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
                            <CatIcon size={15} className={style.text} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-ink">{cat.name}</span>
                              <span className={`text-xs font-bold ${style.text}`}>{cat.score}/100</span>
                            </div>
                          </div>
                          <StatusIcon size={16} className={style.text} />
                          {isExpanded ? <ChevronUp size={14} className="text-slate" /> : <ChevronDown size={14} className="text-slate" />}
                        </button>
                        {isExpanded && (
                          <div className="px-4 pb-4">
                            <div className="bg-white rounded-lg p-3 space-y-1.5">
                              {cat.findings.map((f, fi) => (
                                <p key={fi} className="text-xs text-slate leading-relaxed flex items-start gap-2">
                                  <span className="mt-0.5 flex-shrink-0">•</span> {f}
                                </p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Suggestions */}
                  {result.suggestions.length > 0 && (
                    <div className="bg-white rounded-xl border border-sand p-5">
                      <h3 className="font-display font-bold text-ink text-sm flex items-center gap-2 mb-3">
                        <Zap size={14} className="text-coral" /> Top Improvements
                      </h3>
                      <div className="space-y-2">
                        {result.suggestions.map((s, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs text-slate">
                            <span className="w-5 h-5 bg-coral/10 text-coral rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold mt-0.5">{i + 1}</span>
                            <span className="leading-relaxed">{s}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* What we check */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">
            What We <span className="text-coral">Analyze</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { title: "Subject Line", desc: "Length, personalization, emoji usage, question format, and spam trigger words.", icon: Type },
              { title: "Readability", desc: "Word count, sentence length, paragraph structure, and mobile scannability.", icon: Eye },
              { title: "Spam Risk", desc: "Trigger words, excessive caps, punctuation abuse, and known filter patterns.", icon: AlertTriangle },
              { title: "Personalization", desc: "Merge tags, reader-focused language (you/your vs we/our), and relevance signals.", icon: Users },
              { title: "CTA Strength", desc: "Call-to-action clarity, urgency language, power words, and conversion copy.", icon: Target },
              { title: "Overall Score", desc: "Weighted combination of all five categories — A through F grade with recommendations.", icon: BarChart3 },
            ].map(item => (
              <div key={item.title} className="bg-paper rounded-xl p-5 border border-sand text-center">
                <item.icon size={20} className="mx-auto text-coral mb-3" />
                <h3 className="font-display font-bold text-ink text-sm mb-1.5">{item.title}</h3>
                <p className="text-xs text-slate leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-ink text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <h2 className="font-display text-3xl font-bold text-white mb-3">
            Write Better Emails, Automatically
          </h2>
          <p className="text-white/50 text-base mb-8 leading-relaxed">
            MoreMail's AI writes optimized subject lines, body copy, and CTAs for you.
            Plus pre-built campaigns and automations ready to send.
          </p>
          <div className="flex justify-center mb-6">
            <LeadCaptureBox />
          </div>
          <p className="text-white/30 text-xs">No credit card required • Free plan includes 1,000 subscribers</p>
        </div>
      </section>
    </>
  );
}
